import React, { PropTypes } from 'react';
import Immutable from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { FormattedMessage, FormattedHTMLMessage } from 'react-intl';
import BcReactComponent from '@bibliocommons/base-bc-react-component';
import { withRouter } from 'react-router';
import { shelfItemShape, userIdentityShape, ugcIOwnThisShape } from '@bibliocommons/bc-prop-types';
import { Dropdown, DropdownMenuDivider, DropdownMenuHeader, DropdownMenuItem } from '@bibliocommons/base-dropdown';
import Checkbox from '@bibliocommons/base-checkbox';
import * as Icons from '@bibliocommons/base-icons';
import { SHELF_NAMES } from '../../../../constants/ShelfConstants';

import './OptionsDropdown.scss';

function renderAddToShelfButtonContent(name, onShelf = false) {
  return (
    <span className="on-shelf-label">
      <Icons.ShelfItem className="button-content-icon-shelfitem" width={20} height={19} onShelf={onShelf} />
      <FormattedMessage id={`label_shelf_${name}`} />
    </span>
  );
}

function getShelfOptions() {
  return Immutable.Map(SHELF_NAMES).valueSeq().toArray().map(name => ({
    label: renderAddToShelfButtonContent(name),
    value: name
  }));
}

export class ShelfListItemOptionsDropdown extends BcReactComponent {
  constructor(props) {
    super(props);
    this.bindAll('renderMovableShelfOptions', 'renderOnShelfOptions', 'renderSelectedLabel', 'postSuccess', 'handleOwnershipToggle', 'handlePrivacyToggle');
  }

  handleOwnershipToggle(flagAsOwned) {
    const { currentUserShelfItem, iOwnThisActions, ownerSettingForBib } = this.props;

    if (flagAsOwned) {
      iOwnThisActions.createIOwnThisSetting(currentUserShelfItem.get('metadataId'));
    } else {
      iOwnThisActions.removeIOwnThisSetting(currentUserShelfItem.get('metadataId'), ownerSettingForBib.get('id'));
    }
  }

  handlePrivacyToggle(status) {
    const { currentUserShelfItem, updateShelfItem } = this.props;

    updateShelfItem({
      shelfName: currentUserShelfItem.get('shelf'),
      shelfItemIds: [currentUserShelfItem.get('id')],
      metadataIds: [currentUserShelfItem.get('metadataId')],
      privateItem: status,
      newShelfName: currentUserShelfItem.get('shelf')
    });
  }

  renderSelectedLabel() {
    const { owner, currentUserShelfItem } = this.props;

    if (owner.get('loggedIn')) {
      return (
        <span className="manage-item-label">
          <FormattedMessage id="manage_item" />
        </span>
      );
    }

    return renderAddToShelfButtonContent(currentUserShelfItem.get('shelf'), true);
  }

  handleShelfMove(newShelf) {
    const shelfName = this.props.currentUserShelfItem.get('shelf');
    const id = this.props.currentUserShelfItem.get('id');
    const metadataId = this.props.currentUserShelfItem.get('metadataId');
    const privateItem = this.props.currentUserShelfItem.get('privateItem');

    this.props.updateShelfItem({
      shelfName,
      privateItem,
      shelfItemIds: [id],
      metadataIds: [metadataId],
      newShelfName: newShelf,
      postSuccess: this.postSuccess
    });
  }

  handleRemoveFromShelf() {
    const shelfName = this.props.currentUserShelfItem.get('shelf');
    const id = this.props.currentUserShelfItem.get('id');
    const metadataId = this.props.currentUserShelfItem.get('metadataId');

    this.props.removeShelfItem({
      shelfName,
      shelfItemIds: [id],
      metadataIds: [metadataId],
      postSuccess: this.postSuccess
    });
  }

  postSuccess() {
    const { location, params, owner, searchUserShelf } = this.props;

    if (owner.get('loggedIn')) {
      searchUserShelf(params.shelfName, owner.get('id'), location.query);
    }
  }

  renderMovableShelfOptions() {
    const shelf = this.props.currentUserShelfItem.get('shelf');

    return getShelfOptions()
      .filter(option => option.value !== shelf)
      .map((option) => {
        const handleShelfMove = (e) => {
          e.preventDefault();
          this.handleShelfMove(option.value);
        };

        return (
          <DropdownMenuItem key={`on-shelf-dropdown-item-move-${option.value}`}>
            <a href="#move-to-shelf" onClick={handleShelfMove}>
              {option.label}
            </a>
          </DropdownMenuItem>
        );
      });
  }

  renderOnShelfOptions() {
    const { owner, ownerSettingForBib, currentUserShelfItem } = this.props;

    const heading = (
      <DropdownMenuHeader key="on-shelf-dropdown-header">
        <FormattedHTMLMessage id="change_shelf_location" />
      </DropdownMenuHeader>
    );

    const handleRemoveFromShelf = (e) => {
      e.preventDefault();
      this.handleRemoveFromShelf();
    };

    const deleteOption = (
      <DropdownMenuItem key="on-shelf-dropdown-delete">
        <a href="#remove-from-shelf" className="shelf-delete" onClick={handleRemoveFromShelf}>
          <FormattedMessage id="button_remove_from_shelves" />
        </a>
      </DropdownMenuItem>
    );

    const ownershipMenuItem = (
      <DropdownMenuItem key="item-settings-ownership" className="item-settings">
        <Checkbox
          id="item-settings-ownership"
          handleChange={this.handleOwnershipToggle}
          checked={!!ownerSettingForBib}
          label={<FormattedMessage id="button_i_own_this" />} />
      </DropdownMenuItem>
    );

    const privacyMenuItem = (
      <DropdownMenuItem key="item-settings-privacy" className="item-settings">
        <Checkbox
          id="item-settings-privacy"
          handleChange={this.handlePrivacyToggle}
          checked={currentUserShelfItem.get('privateItem')}
          label={<FormattedMessage id="button_mark_as_private" />} />
      </DropdownMenuItem>
    );

    const ownerHeading = <DropdownMenuHeader key="move-to-shelf-label"><FormattedMessage id="move_to_shelf" /></DropdownMenuHeader>;
    const deleteDivider = <DropdownMenuDivider key="on-shelf-dropdown-delete-divider" />;
    const settingsDivider = <DropdownMenuDivider key="on-shelf-dropdown-settings-divider" />;

    return ([
      owner.get('loggedIn') ? ownerHeading : heading,
      this.renderMovableShelfOptions(),
      owner.get('loggedIn') ? deleteDivider : null,
      owner.get('loggedIn') ? deleteOption : null,
      owner.get('loggedIn') ? settingsDivider : null,
      owner.get('loggedIn') ? ownershipMenuItem : null,
      owner.get('loggedIn') ? privacyMenuItem : null
    ]);
  }

  render() {
    const { shelfTransactions, currentUserShelfItem, iOwnThisTransactions } = this.props;
    const metadataId = currentUserShelfItem.get('metadataId');
    const isMoving = shelfTransactions.getIn([metadataId, 'isMoving'], false);
    const isRemoving = shelfTransactions.getIn([metadataId, 'isRemoving'], false);
    const isUpdatingPrivacy = shelfTransactions.getIn([metadataId, 'isUpdatingPrivacy'], false);
    const isUpdatingOwnership = iOwnThisTransactions.getIn([metadataId, 'isAdding'], false) || iOwnThisTransactions.getIn([metadataId, 'isRemoving'], false);
    const isLoading = isMoving || isRemoving || isUpdatingPrivacy || isUpdatingOwnership;

    return (
      <div className="cp-shelf-list-item-options-dropdown">
        <Dropdown
          isLoading={isLoading}
          className="btn-default btn-block btn-on-shelf"
          selected={this.renderSelectedLabel()}>
          {this.renderOnShelfOptions()}
        </Dropdown>
      </div>
    );
  }
}

ShelfListItemOptionsDropdown.propTypes = {
  currentUserShelfItem: shelfItemShape,
  updateShelfItem: PropTypes.func.isRequired,
  removeShelfItem: PropTypes.func.isRequired,
  searchUserShelf: PropTypes.func.isRequired,
  iOwnThisActions: PropTypes.objectOf(PropTypes.func).isRequired,
  shelfTransactions: ImmutablePropTypes.map.isRequired,
  owner: userIdentityShape.isRequired,
  iOwnThisTransactions: ImmutablePropTypes.map.isRequired,
  ownerSettingForBib: ugcIOwnThisShape
};

export default withRouter(ShelfListItemOptionsDropdown);



// WEBPACK FOOTER //
// ./app/components/shelves/ShelfListItem/OptionsDropdown/OptionsDropdown.jsx